import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "config/axiosInstance";
import { saveUser, getAccessToken, getDefaultHomePath } from "utils/auth";
import { normalizeLoginIdentifier, looksLikePhone } from "utils/phone";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiSwitch from "components/VuiSwitch";
import { useAppAlert } from "hooks/useAppAlert";
import GradientBorder from "examples/GradientBorder";
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgSignIn from "assets/images/signInImage.png";

function SignIn() {
  const [correo, setCorreo] = useState("");
  const [password, setpassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetStep, setResetStep] = useState(1);
  const history = useHistory();
  const { showAlert, AlertBanner } = useAppAlert();

  useEffect(() => {
    if (getAccessToken()) {
      history.replace(getDefaultHomePath());
    }
  }, [history]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
        const loginId = looksLikePhone(correo) ? normalizeLoginIdentifier(correo) : correo.trim();
        const res = await axiosInstance.post("/auth/login", {
          correo: loginId,
          password,
        });

      if (res.data.success) {
        const { user, accessToken, refreshToken } = res.data;

        saveUser(user, accessToken, refreshToken, rememberMe);
        history.push(getDefaultHomePath());
      } else {
        showAlert(res.data.message || "Credenciales incorrectas", "error");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error al conectar con el servidor";
      showAlert(errorMsg, "error");
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail || resetEmail.trim() === "") {
      showAlert("Por favor ingresa tu correo electrónico", "warning");
      return;
    }

    try {
      const res = await axiosInstance.post("/auth/request-password-reset", {
        email: resetEmail.trim(),
      });

      if (res.data.success) {
        showAlert(res.data.message || "Código de verificación enviado a tu correo", "success");
        setResetStep(2);
      } else {
        showAlert(res.data.message || "Error al solicitar código", "error");
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al solicitar código de verificación", "error");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      showAlert("La contraseña debe tener al menos 8 caracteres", "warning");
      return;
    }

    if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      showAlert("La contraseña debe incluir al menos una letra y un número", "warning");
      return;
    }

    try {
      const res = await axiosInstance.post("/auth/reset-password", {
        email: resetEmail,
        resetCode,
        newPassword,
      });

      if (res.data.success) {
        showAlert("Contraseña restablecida exitosamente. Ahora puedes iniciar sesión", "success");
        setTimeout(() => {
          setShowResetPassword(false);
          setResetStep(1);
          setResetEmail("");
          setResetCode("");
          setNewPassword("");
        }, 2000);
      } else {
        showAlert(res.data.message || "Error al restablecer contraseña", "error");
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error al restablecer contraseña", "error");
    }
  };

  if (showResetPassword) {
    return (
      <CoverLayout
        title="Recuperar Contraseña"
        color="white"
        description={resetStep === 1 ? "Ingresa tu correo electrónico" : "Ingresa el código y tu nueva contraseña"}
        premotto="CUENTAMELON"
        motto="GESTIÓN FINANCIERA SIMPLE"
        image={bgSignIn}
        showNavbar={false}
      >
        <AlertBanner />
        
        {resetStep === 1 ? (
          <VuiBox component="form" role="form" onSubmit={handleRequestReset}>
            <VuiBox mb={2}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Correo electrónico
              </VuiTypography>
              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius={borders.borderRadius.lg}
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type="email"
                  placeholder="tu-correo@ejemplo.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  fontWeight="500"
                  required
                />
              </GradientBorder>
            </VuiBox>

            <VuiBox mt={4} mb={1}>
              <VuiButton color="info" fullWidth type="submit">
                ENVIAR CÓDIGO
              </VuiButton>
            </VuiBox>

            <VuiBox mt={2}>
              <VuiButton 
                color="text" 
                fullWidth 
                onClick={() => setShowResetPassword(false)}
                sx={{ textDecoration: "underline" }}
              >
                Volver al inicio de sesión
              </VuiButton>
            </VuiBox>
          </VuiBox>
        ) : (
          <VuiBox component="form" role="form" onSubmit={handleResetPassword}>
            <VuiBox mb={2}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Código de verificación
              </VuiTypography>
              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius={borders.borderRadius.lg}
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type="text"
                  placeholder="Código de 6 dígitos"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  fontWeight="500"
                  required
                />
              </GradientBorder>
            </VuiBox>

            <VuiBox mb={2}>
              <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
                Nueva Contraseña
              </VuiTypography>
              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius={borders.borderRadius.lg}
                backgroundImage={radialGradient(
                  palette.gradients.borderLight.main,
                  palette.gradients.borderLight.state,
                  palette.gradients.borderLight.angle
                )}
              >
                <VuiInput
                  type="password"
                  placeholder="Nueva contraseña"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </GradientBorder>
            </VuiBox>

            <VuiBox mt={4} mb={1}>
              <VuiButton color="info" fullWidth type="submit">
                RESTABLECER CONTRASEÑA
              </VuiButton>
            </VuiBox>

            <VuiBox mt={2}>
              <VuiButton 
                color="text" 
                fullWidth 
                onClick={() => {
                  setShowResetPassword(false);
                  setResetStep(1);
                }}
                sx={{ textDecoration: "underline" }}
              >
                Volver al inicio de sesión
              </VuiButton>
            </VuiBox>
          </VuiBox>
        )}
      </CoverLayout>
    );
  }

  return (
    <CoverLayout
      title="Bienvenido"
      color="white"
      description="Ingrese su correo o número de celular y contraseña"
      premotto="CUENTAMELON"
      motto="GESTIÓN FINANCIERA SIMPLE"
      image={bgSignIn}
      showNavbar={false}
    >
      <AlertBanner />
      
      <VuiBox component="form" role="form" onSubmit={handleLogin}>
        <VuiBox mb={2}>
          <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
            Correo o número celular
          </VuiTypography>
          <GradientBorder
            minWidth="100%"
            padding="1px"
            borderRadius={borders.borderRadius.lg}
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput
              type="text"
              placeholder="Correo o número celular"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              fontWeight="500"
              required
            />
          </GradientBorder>
        </VuiBox>

        <VuiBox mb={2}>
          <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
            Contraseña
          </VuiTypography>
          <GradientBorder
            minWidth="100%"
            padding="1px"
            borderRadius={borders.borderRadius.lg}
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput
              type="password"
              placeholder="Tu contraseña..."
              value={password }
              onChange={(e) => setpassword (e.target.value)}
              required
            />
          </GradientBorder>
        </VuiBox>

        <VuiBox display="flex" alignItems="center">
          <VuiSwitch color="info" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />
          <VuiTypography
            variant="caption"
            color="white"
            fontWeight="medium"
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;Recordarme
          </VuiTypography>
        </VuiBox>

        <VuiBox mt={4} mb={1}>
          <VuiButton color="info" fullWidth type="submit">
            INGRESAR
          </VuiButton>
        </VuiBox>

        <VuiBox mt={2} textAlign="center">
          <VuiTypography
            variant="button"
            color="text"
            fontWeight="regular"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setShowResetPassword(true)}
          >
            ¿Olvidaste tu contraseña?
          </VuiTypography>
        </VuiBox>

        {/* Registration removed: users will not self-register */}
      </VuiBox>
    </CoverLayout>
  );
}

export default SignIn;
